import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type Squad = {
  id: string;
  slug: string;
  name: string;
  description: string;
  is_public: boolean;
  owner_user_id: string;
  member_count?: number;
};

// Lists squads the current user belongs to (joined via membership).
export function useMySquads() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["squads", "mine", user?.id],
    enabled: !!user,
    queryFn: async (): Promise<Squad[]> => {
      const { data, error } = await supabase
        .from("squad_members")
        .select("squads!inner(id, slug, name, description, is_public, owner_user_id)")
        .eq("user_id", user!.id);
      if (error) throw error;
      return (data ?? []).map((r: any) => r.squads as Squad);
    },
  });
}

export function useSquadBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ["squad", slug],
    enabled: !!slug,
    queryFn: async (): Promise<Squad | null> => {
      const { data, error } = await supabase
        .from("squads")
        .select("*")
        .eq("slug", slug!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useSquadChannels(squadId: string | undefined) {
  return useQuery({
    queryKey: ["squad-channels", squadId],
    enabled: !!squadId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("squad_channels")
        .select("*")
        .eq("squad_id", squadId!)
        .order("position", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useSquadMembers(squadId: string | undefined) {
  return useQuery({
    queryKey: ["squad-members", squadId],
    enabled: !!squadId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("squad_members")
        .select("user_id, role, joined_at, profiles:profiles!inner(username, display_name, avatar_url)")
        .eq("squad_id", squadId!)
        .order("joined_at", { ascending: true });
      if (error) {
        // Fallback if relationship inference fails
        const { data: simple } = await supabase
          .from("squad_members")
          .select("user_id, role, joined_at")
          .eq("squad_id", squadId!);
        return simple ?? [];
      }
      return data ?? [];
    },
  });
}

// Squad-level weekly leaderboard
export function useSquadLeaderboard() {
  return useQuery({
    queryKey: ["squad-leaderboard"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("squad_weekly_leaderboard" as any)
        .select("*")
        .order("weekly_xp_total", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data ?? [];
    },
  });
}
